import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

const examples = [
{label:'Uzupełnienie. Pierwiastek nieparzystego stopnia',summary:'Liczba pod pierwiastkiem może być ujemna',content:(<><BlockMath math={String.raw`\sqrt[3]{-8}+\sqrt[5]{-32}=-2-2=-4`} /><p>Pierwiastek parzystego stopnia z liczby ujemnej nie istnieje w liczbach rzeczywistych.</p></>)},

  {
    label: "Przykład 1. Wyłączanie czynnika z pierwiastka kwadratowego",
    summary: "√72",
    content: (
      <>
        <p>Rozkładamy liczbę 72 na czynniki pierwsze:</p>
        <BlockMath math={String.raw`\begin{array}{r|l}
72&2\\36&2\\18&2\\9&3\\3&3\\1&
\end{array}`} />
        <BlockMath math={String.raw`72=2\cdot 2\cdot 2\cdot 3\cdot 3.`} />
        <p>
          Pod pierwiastkiem kwadratowym szukamy par jednakowych czynników.
          Para dwójek oraz para trójek dają przed pierwiastkiem odpowiednio
          dwójkę i trójkę:
        </p>
        <BlockMath
          math={String.raw`\sqrt{72}
            =\sqrt{\underbrace{2\cdot 2}_{\text{para}}\cdot 2\cdot
            \underbrace{3\cdot 3}_{\text{para}}}`}
        />
        <BlockMath
          math={String.raw`=2\cdot3\sqrt2=6\sqrt2.`}
        />
      </>
    ),
  },
  {
    label: "Przykład 2. Wyłączanie czynnika z pierwiastka sześciennego",
    summary: "∛54",
    content: (
      <>
        <p>Rozkładamy liczbę 54 na czynniki pierwsze:</p>
        <BlockMath math={String.raw`\begin{array}{r|l}
54&2\\27&3\\9&3\\3&3\\1&
\end{array}`} />
        <BlockMath math={String.raw`54=2\cdot 3\cdot 3\cdot 3.`} />
        <p>
          Pod pierwiastkiem sześciennym szukamy trójek jednakowych czynników.
          Trójka trójek daje jedną trójkę przed pierwiastkiem:
        </p>
        <BlockMath
          math={String.raw`\sqrt[3]{54}
            =\sqrt[3]{2\cdot\underbrace{3\cdot 3\cdot 3}_{\text{trójka}}}`}
        />
        <BlockMath
          math={String.raw`=\sqrt[3]{3^3\cdot 2}=3\sqrt[3]{2}.`}
        />
      </>
    ),
  },
  {
    label: "Przykład 3. Dodawanie pierwiastków kwadratowych",
    summary: "√72 + √18",
    content: (
      <>
        <p>Rozkładamy obie liczby na czynniki pierwsze:</p>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap", justifyContent: "center" }}>
          <BlockMath math={String.raw`\begin{array}{r|l}
72&2\\36&2\\18&2\\9&3\\3&3\\1&
\end{array}`} />
          <BlockMath math={String.raw`\begin{array}{r|l}
18&2\\9&3\\3&3\\1&
\end{array}`} />
        </div>
        <BlockMath math={String.raw`72=2\cdot2\cdot2\cdot3\cdot3`} />
        <BlockMath math={String.raw`18=2\cdot3\cdot3`} />
        <p>
          Z każdej pary jednakowych czynników wyjmujemy jeden czynnik przed
          pierwiastek:
        </p>
        <BlockMath
          math={String.raw`\sqrt{72}=2\sqrt{18}=2\cdot3\sqrt2=6\sqrt2`}
        />
        <BlockMath math={String.raw`\sqrt{18}=3\sqrt2`} />
        <BlockMath
          math={String.raw`\sqrt{72}+\sqrt{18}=6\sqrt2+3\sqrt2=9\sqrt2.`}
        />
      </>
    ),
  },
  {
    label: "Przykład 4. Dodawanie pierwiastków sześciennych",
    summary: "∛24 + ∛192",
    content: (
      <>
        <p>Rozkładamy obie liczby na czynniki pierwsze:</p>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap", justifyContent: "center" }}>
          <BlockMath math={String.raw`\begin{array}{r|l}
24&2\\12&2\\6&2\\3&3\\1&
\end{array}`} />
          <BlockMath math={String.raw`\begin{array}{r|l}
192&2\\96&2\\48&2\\24&2\\12&2\\6&2\\3&3\\1&
\end{array}`} />
        </div>
        <BlockMath math={String.raw`24=2\cdot2\cdot2\cdot3`} />
        <BlockMath math={String.raw`192=2\cdot2\cdot2\cdot2\cdot2\cdot2\cdot3`} />
        <p>
          Z każdej trójki jednakowych czynników wyjmujemy jeden czynnik przed
          pierwiastek sześcienny:
        </p>
        <BlockMath
          math={String.raw`\sqrt[3]{24}+\sqrt[3]{192}
            =\sqrt[3]{2^3\cdot 3}+\sqrt[3]{4^3\cdot 3}`}
        />
        <BlockMath
          math={String.raw`=2\sqrt[3]{3}+4\sqrt[3]{3}=6\sqrt[3]{3}.`}
        />
      </>
    ),
  },
  {
    label: "Przykład 5. Kwadrat dwumianu z pierwiastkiem",
    summary: "(√3 + 1)² − √12",
    content: (
      <>
        <p>Korzystamy ze wzoru skróconego mnożenia:</p>
        <BlockMath math={String.raw`(a+b)^2=a^2+2ab+b^2.`} />
        <BlockMath
          math={String.raw`(\sqrt{3}+1)^2=3+2\sqrt{3}+1=4+2\sqrt{3}.`}
        />
        <p>Ponieważ <InlineMath math={String.raw`\sqrt{12}=2\sqrt{3}`} />, mamy:</p>
        <BlockMath
          math={String.raw`(\sqrt{3}+1)^2-\sqrt{12}
            =4+2\sqrt{3}-2\sqrt{3}=4.`}
        />
      </>
    ),
  },

{label:'Uzupełnienie. Iloczyn i iloraz pierwiastków',summary:'Łączymy pierwiastki tego samego stopnia',content:(<><BlockMath math={String.raw`\sqrt{6}\cdot\sqrt{24}=\sqrt{144}=12`} /><BlockMath math={String.raw`\frac{\sqrt{75}}{\sqrt3}=\sqrt{25}=5`} /><p>Łączymy pierwiastki tego samego stopnia. Nie stosujemy tej reguły do sumy.</p></>)},
{label:'Uzupełnienie. Dwa podobne zapisy, różne wyniki',summary:'Kwadraty wyrażeń przeciwnych i sprzężonych',content:(<><BlockMath math={String.raw`(2-\sqrt3)^2-(\sqrt3-2)^2=0`} /><p>Nawiasy są liczbami przeciwnymi, więc mają równe kwadraty.</p><BlockMath math={String.raw`(1+\sqrt5)^2-(1-\sqrt5)^2=4\sqrt5`} /><p>W drugim wyrażeniu można zastosować różnicę kwadratów; nawiasy nie są liczbami przeciwnymi.</p></>)},


];

const RootOperationsPreparationTheory = () => (
  <TheoryExamplesRunner
    title="Działania na pierwiastkach"
    intro={
      <>
        <p>
          Pierwiastek kwadratowy z liczby nieujemnej <InlineMath math="a" /> to
          taka liczba nieujemna <InlineMath math="b" />, której kwadrat jest
          równy <InlineMath math="a" />.
        </p>
        <BlockMath
          math={String.raw`\sqrt{a}=b\quad\Longleftrightarrow\quad b^2=a,\quad b\geq 0.`}
        />
        <p>
          Pierwiastek sześcienny z liczby <InlineMath math="a" /> to taka
          liczba <InlineMath math="b" />, której sześcian jest równy{" "}
          <InlineMath math="a" />.
        </p>
        <BlockMath
          math={String.raw`\sqrt[3]{a}=b\quad\Longleftrightarrow\quad b^3=a.`}
        />
        <p>
          Aby wyłączyć czynnik przed znak pierwiastka, rozkładamy liczbę pod
          pierwiastkiem na czynniki pierwsze. Dla pierwiastka kwadratowego
          szukamy par jednakowych czynników, a dla sześciennego — trójek.
        </p>
        <BlockMath
          math={String.raw`\sqrt{a^2b}=a\sqrt{b}\quad\text{dla }a\geq 0`}
        />
        <BlockMath
          math={String.raw`\sqrt[3]{a^3b}=a\sqrt[3]{b}`}
        />
        <p>
          Dla pierwiastków kwadratowych mnożenie i dzielenie pod jednym znakiem stosujemy przy nieujemnych liczbach pod pierwiastkami, a w mianowniku wymagamy liczby dodatniej.
          Konkretne rozkłady oraz dalsze działania zobaczysz w przykładach.
        </p>
      </>
    }
    examples={examples}
  />
);

export default RootOperationsPreparationTheory;
